import {
    ChildId,
    CostAllocationRefersTo,
    MonthlySettlementCostSplitDto,
    SummaryInvoiceDataDto,
} from 'types';
import { DetailsColumn, DetailsFrameBase, DetailsRow } from 'components/atoms';
import { settlementTexts } from 'consts/text';
import styled, { css } from 'styled-components';
import { AppCardList } from 'components/organisms';
import { useMemo } from 'react';
import { useAppQuery } from 'services/reactQuery/useAppQuery';

import { payerMapper } from 'consts/settlementChildren/payerMapper';
import { referToMapper } from 'consts/settlementChildren/referToMapper';
import ParentHTTPService from 'services/HTTPService/parent/ParentHTTPService';
import { AppCardListItem } from 'components/atoms/CardList/AppListCardItem/AppCardListItem';
import { useIsMobile } from 'hooks/useIsMobile/useIsMobile';
import { PriceDisplay } from '../../../molecules/Table/PriceDisplay/PriceDisplay';
import { AdditionalCostInvoice } from '../ChildSettlementDetails/AdditionalCostList/AdditionalCostItem/AdditionalCostDetails/AdditionalCostInvoice';

export const ItemContainer = styled.div`
    margin-block-start: 32px;
`;

const StyledDetailsFrameBase = styled(DetailsFrameBase)`
    ${({ theme }) => css`
        @media (${theme.sizes.mediaQueries.mobileMax}) {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            gap: ${theme.sizes.marginNormal}px;
        }
    `}
`;

type Props = {
    title: string;
    period?: string;
    correctionId?: string;
    childId: ChildId;
};

export const ParentSettlementCostSplitList = ({ title, period, correctionId, childId }: Props) => {
    const isMobile = useIsMobile();
    const {
        data: childSettlement,
        isLoading,
        isFetching,
    } = useAppQuery(
        'CHILD_SETTLEMENT',
        [period, childId],
        () =>
            ParentHTTPService.settlements.getChildSettlement({
                period,
            }),
        {},
    );

    const costSplits = useMemo(() => {
        if (correctionId) {
            const correction = childSettlement?.corrections?.find(
                (corr) => corr.id === correctionId,
            );
            return correction?.costSplits || [];
        }
        return childSettlement?.monthlySettlementDetailsDto?.costSplits || [];
    }, [childSettlement, correctionId]);

    if (isLoading || isFetching || costSplits?.length === 0) {
        return null;
    }

    return (
        <ItemContainer>
            <StyledDetailsFrameBase hideHeader={isMobile}>
                <AppCardList
                    marginTop={-32}
                    title={title}
                    isHiddenAddButton
                    dataSource={costSplits}
                    emptyInfo={settlementTexts.children.costAllocation.emptyInfo}
                    renderItem={(record: MonthlySettlementCostSplitDto, index) => (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                gap: 16,
                                alignItems: 'stretch',
                                width: '100%',
                            }}
                        >
                            <div style={{ flex: 1 }}>
                                <AppCardListItem key={record.id} index={index} listItem={record}>
                                    <DetailsRow>
                                        <DetailsColumn
                                            title={
                                                settlementTexts.children.costSplit.institutionName
                                            }
                                            span={16}
                                        >
                                            {record.foundationName}
                                        </DetailsColumn>
                                    </DetailsRow>
                                    <DetailsRow>
                                        <DetailsColumn
                                            title={settlementTexts.children.costSplit.nip}
                                            span={6}
                                        >
                                            {record.nip}
                                        </DetailsColumn>
                                        <DetailsColumn
                                            title={settlementTexts.children.costSplit.finalAmount}
                                            span={6}
                                        >
                                            {record.finalGrossAmount ? (
                                                <PriceDisplay value={record.finalGrossAmount} />
                                            ) : (
                                                '-'
                                            )}
                                        </DetailsColumn>
                                        <DetailsColumn
                                            title={
                                                settlementTexts.children.costSplit.percentageAmount
                                            }
                                            span={6}
                                        >
                                            {record.percentageAmount
                                                ? `${record.percentageAmount} %`
                                                : '-'}
                                        </DetailsColumn>
                                        <DetailsColumn
                                            title={settlementTexts.children.costSplit.payerType}
                                            span={6}
                                        >
                                            {payerMapper[record.payerType]}
                                        </DetailsColumn>
                                    </DetailsRow>
                                    <DetailsRow>
                                        <DetailsColumn
                                            title={settlementTexts.children.costSplit.refersTo}
                                            span={6}
                                        >
                                            {
                                                referToMapper[
                                                    record.costCategory as CostAllocationRefersTo
                                                ]
                                            }
                                        </DetailsColumn>
                                        <DetailsColumn
                                            title={settlementTexts.children.costSplit.invoiceNotes}
                                            span={12}
                                        >
                                            {record.invoiceNotes}
                                        </DetailsColumn>
                                    </DetailsRow>
                                </AppCardListItem>
                            </div>

                            {(
                                record as MonthlySettlementCostSplitDto & {
                                    invoiceData?: SummaryInvoiceDataDto;
                                }
                            ).invoiceData && (
                                <div
                                    style={{
                                        width: isMobile ? '100%' : 320,
                                        flexShrink: 0,
                                        display: 'flex',
                                    }}
                                >
                                    <AdditionalCostInvoice
                                        childId={childId}
                                        invoiceData={
                                            (
                                                record as MonthlySettlementCostSplitDto & {
                                                    invoiceData: SummaryInvoiceDataDto;
                                                }
                                            ).invoiceData
                                        }
                                        isParent
                                        title="Rozliczenie"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                />
            </StyledDetailsFrameBase>
        </ItemContainer>
    );
};
